import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../components/apiconfig/api-config';

const DEFAULT_TIMEOUT = 30 * 60 * 1000;
const DEFAULT_WARNING_LEAD = 60 * 1000;
const DEADLINE_CHECK_INTERVAL = 15 * 1000;
const IDLE_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'touchend',
  'pointerdown',
  'click',
  'focus',
];
const LOGOUT_DEADLINE_KEY = 'auto_logout_deadline';

const safeStorage = {
  set: (key, value) => { try { localStorage.setItem(key, value); } catch (e) {} },
  get: (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } },
  remove: (key) => { try { localStorage.removeItem(key); } catch (e) {} },
};

const useAutoLogout = ({ timeout = DEFAULT_TIMEOUT, warningLeadTime = DEFAULT_WARNING_LEAD } = {}) => {
  const navigate = useNavigate();
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const deadlinePoller = useRef(null);
  const warningToastId = useRef(null);
  const hasLoggedOut = useRef(false);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(deadlinePoller.current);
    warningTimer.current = null;
    logoutTimer.current = null;
    deadlinePoller.current = null;

    if (warningToastId.current) {
      toast.dismiss(warningToastId.current);
      warningToastId.current = null;
    }
  }, []);

  const triggerLogout = useCallback(async () => {
    if (hasLoggedOut.current) return;
    hasLoggedOut.current = true;

    clearTimers();
    safeStorage.remove(LOGOUT_DEADLINE_KEY);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('Auto logout server call failed:', err);
    }

    try { localStorage.removeItem('token'); } catch (e) {}
    try { sessionStorage.removeItem('token'); } catch (e) {}

    if (axios.defaults.headers?.common?.Authorization) {
      delete axios.defaults.headers.common.Authorization;
    }

    toast.info('You were logged out after 30 minutes of inactivity.', {
      position: 'top-right',
      autoClose: 3000,
    });

    navigate('/', { replace: true });
  }, [clearTimers, navigate]);

  const scheduleFromDeadline = useCallback((deadline) => {
    if (hasLoggedOut.current) return;

    clearTimers();

    const remaining = deadline - Date.now();

    if (remaining <= 0) {
      triggerLogout();
      return;
    }

    safeStorage.set(LOGOUT_DEADLINE_KEY, String(deadline));

    const warningDelay = Math.max(remaining - warningLeadTime, 0);

    if (warningDelay > 0) {
      warningTimer.current = setTimeout(() => {
        if (hasLoggedOut.current) return;

        warningToastId.current = toast.warning(
          'You will be logged out in one minute if there is no activity.',
          { position: 'top-right', autoClose: warningLeadTime }
        );
      }, warningDelay);
    }

    logoutTimer.current = setTimeout(triggerLogout, remaining);
  }, [clearTimers, triggerLogout, warningLeadTime]);

  const scheduleTimers = useCallback(() => {
    if (hasLoggedOut.current) return;
    scheduleFromDeadline(Date.now() + timeout);
  }, [scheduleFromDeadline, timeout]);

  const checkDeadlineOnResume = useCallback(() => {
    if (hasLoggedOut.current) return true;

    const stored = safeStorage.get(LOGOUT_DEADLINE_KEY);
    if (!stored) return false;

    const deadline = Number(stored);
    if (!Number.isFinite(deadline)) return false;

    if (Date.now() >= deadline) {
      triggerLogout();
      return true;
    }

    scheduleFromDeadline(deadline);
    return true;
  }, [scheduleFromDeadline, triggerLogout]);

  useEffect(() => {
    const hadDeadline = checkDeadlineOnResume();
    if (hasLoggedOut.current) return;
    if (!hadDeadline) scheduleTimers();

    const handleActivity = () => {
      if (!document.hidden && !hasLoggedOut.current) {
        scheduleTimers();
      }
    };

    const handleResume = () => {
      if (!document.hidden) {
        checkDeadlineOnResume();
      }
    };

    deadlinePoller.current = setInterval(() => {
      if (!document.hidden) {
        checkDeadlineOnResume();
      }
    }, DEADLINE_CHECK_INTERVAL);

    IDLE_EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('pageshow', handleResume);
    window.addEventListener('focus', handleResume);
    window.addEventListener('online', handleResume);

    return () => {
      IDLE_EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('pageshow', handleResume);
      window.removeEventListener('focus', handleResume);
      window.removeEventListener('online', handleResume);
      clearTimers();
    };
  }, [checkDeadlineOnResume, scheduleTimers, clearTimers]);
};

export default useAutoLogout;
